import React, { useState, useCallback, useEffect, useMemo } from "react";

import HeatMap from "./components/HeatMap";
import FilterChart from "./components/FilterChart";
import FilterHeatMap from "./components/FilterHeatMap";

import BookingChart from "../../components/Chart/BookingChart";

import InboomaSubmenu from "../../components/InboomaSubmenu";

import t from "../../helpers/t";
import { _ } from "../../../../vendor";
// @ts-ignore
import {
  useSummary,
  useSummaryForAPeriod,
  useSummaryPerMonth,
  useCountOfBookingsPerMembership,
  useCountOfUsersPerMembership,
} from "../../helpers/useChartsDataOld";
import Rbac from "../../../utils/Rbac";
import Loader from "../../components/Loader";
import defaultChartsConfig from "./components/defaultChartsConfig.json";
import { moment } from "../../../../vendor";
export const rbac = new Rbac({
  _rbac: {
    admin: {
      write: true,
      delete: true,
      widgetUsers: true,
      widgetAvailables: true,
      readAll: true,
    },
    manager: {
      write: true,
      delete: true,
      widgetUsers: true,
      widgetAvailables: true,
      readAll: true,
    },
    "booking-manager": {
      write: true,
      delete: true,
      widgetUsers: true,
      widgetAvailables: true,
      readAll: true,
    },
    "manager-location": {
      write: true,
      delete: true,
      widgetUsers: true,
      widgetAvailables: true,
      readAll: true,
    },
    "garage-staff": {
      write: true,
      delete: false,
      widgetUsers: true,
      widgetAvailables: true,
      readAll: false,
    },
    guide: {
      write: false,
      delete: false,
      widgetUsers: true,
      widgetAvailables: true,
      readAll: false,
    },
    user: {
      write: false,
      delete: false,
      widgetUsers: false,
      widgetAvailables: false,
      readAll: false,
    },
  },
});

/**
 * The chart object is defined as:
 *  {
 *      "title": "total_bookings_per_month",  // I18n string
 *      "channel": "SummaryPerMonth",         // Data channel to use
 *      "key": "totalBooking",                // Key if data channel is object
 *      "type": "bar",                        // Chart type
 *      "hideLegend": true,                   // Hiding legend or not
 *      "isStacked": true                     // Is the chart stacked ("bar" type only)
 *  },
 * For title, see .json files in `src\locales\`
 * For channels and keys, see `src\js\react\helpers\useChartsData.tsx`
 * For type, see `src\js\react\components\Chart\BookingChart.js` and C3.js
 */

export const StatisticsPage = ({ view }) => {
  const [dashboardLayout, setDashboardLayout] = useState(view);

  const [isSummaryInit, setSummaryInit] = useState(false);

  const [isChartInit, setChartInit] = useState(false);

  const fetcher = {
    Summary: useSummary(),
    SummaryForAPeriod: useSummaryForAPeriod(),
    SummaryPerMonth: useSummaryPerMonth(),
    CountOfBookingsPerMembership: useCountOfBookingsPerMembership(),
    CountOfUsersPerMembership: useCountOfUsersPerMembership(),
  };

  const thisMonth = useMemo(() => fetcher.SummaryForAPeriod[0], [fetcher.SummaryForAPeriod]);
  // Needed for isSummaryInit and isChartInit
  const summaryDone = useMemo(() => fetcher.Summary[1], [fetcher.Summary]);
  const summaryForAPeriodDone = useMemo(
    () => fetcher.SummaryForAPeriod[1],
    [fetcher.SummaryForAPeriod],
  );
  const summaryPerMonthDone = useMemo(() => fetcher.SummaryPerMonth[1], [fetcher.SummaryPerMonth]);
  const countOfBookingsPerMembershipDone = useMemo(
    () => fetcher.CountOfBookingsPerMembership[1],
    [fetcher.CountOfBookingsPerMembership],
  );
  const countOfUsersPerMembershipDone = useMemo(
    () => fetcher.CountOfUsersPerMembership[1],
    [fetcher.CountOfUsersPerMembership],
  );

  // Because object, changing anything to date will cause rerender
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const startDate = new Date(currentYear, currentMonth, 1);
  const endDate = new Date(currentYear, currentMonth + 1, 0);
  const [getChartFilter, setChartFilter] = useState({
    from: startDate,
    to: endDate,
  });

  const handleChartsFilter = useCallback((filter) => {
    setChartInit(false);
    setSummaryInit(false);
    setChartFilter((state) => ({ ...state, ...filter }));
  }, []);

  useEffect(() => {
    // Comment below to deactivate fetcher
    Object.values(fetcher).forEach((hook) => hook[2](getChartFilter));
  }, [getChartFilter]);

  useEffect(() => {
    if (!isSummaryInit && summaryForAPeriodDone) {
      setSummaryInit(true);
    }
  }, [summaryForAPeriodDone]);

  useEffect(() => {
    if (
      !isChartInit &&
      summaryForAPeriodDone &&
      summaryPerMonthDone &&
      countOfBookingsPerMembershipDone &&
      countOfUsersPerMembershipDone
    ) {
      setChartInit(true);
    }
  }, [
    summaryForAPeriodDone,
    summaryPerMonthDone,
    countOfBookingsPerMembershipDone,
    countOfUsersPerMembershipDone,
  ]);

  const chartConfig = defaultChartsConfig.charts;

  const chartComps = chartConfig.map(function configToChart(
    { title, key, channel, type, isStacked, hideLegend },
    idx,
  ) {
    let complete = 0;
    const data = key !== undefined ? fetcher[channel][0][key] : fetcher[channel][0];

    const co2SavingsToNorderstedt =
      title === "carbonate_savings" && fetcher[channel][0]["co2SavingsToNorderstedt"] !== null
        ? Math.round(fetcher[channel][0]["co2SavingsToNorderstedt"])
        : 0;
    const co2SavingsFromNorderstedtToLuebeck =
      title === "carbonate_savings" &&
      fetcher[channel][0]["co2SavingsFromNorderstedtToLuebeck"] !== null
        ? Math.round(fetcher[channel][0]["co2SavingsFromNorderstedtToLuebeck"])
        : 0;
    if (data === undefined || data.json === undefined) {
      throw new Error(`No data got from chart: ${title}/${key}/${channel}`);
    }

    if (isChartInit && (type === "pie" || type === "donut")) {
      Object.entries(data.json[0]).map((type) => {
        complete += type[1];
      });
    }
    let classIdentifyChart = "";
    title === "carbonate_savings"
      ? (classIdentifyChart = "carbonate-savings-donut-chart")
      : (classIdentifyChart = "");
    return (
      <div className={`dashboard-chart ${classIdentifyChart}`} key={`chart-${idx}`}>
        {isChartInit ? (
          <>
            <p className="chart-title">{t(title)}</p>
            {((type === "pie" || type === "donut") && complete > 0) ||
            (type !== "pie" && type !== "donut") ? (
              <BookingChart data={data} type={type} isStacked={isStacked} hideLegend={hideLegend} />
            ) : (
              <p className="no-data-chart">{t("no data available")}</p>
            )}
            {title === "carbonate_savings" ? (
              <div className="carbonate_chart_note">
                {/*<p>* {t('${a}_co2_savings_to_short_drives_norderstedt',{a: co2SavingsToNorderstedt},'capital')}</p>
                <p>* {t('${a}_co2_savings_to_norderstedt_luebeck',{a: co2SavingsFromNorderstedtToLuebeck},'capital')}</p>*/}
                <p>
                  *{" "}
                  {t(
                    "${a}_co2_savings_${b}_co2_savings",
                    { a: co2SavingsToNorderstedt, b: co2SavingsFromNorderstedtToLuebeck },
                    "capital",
                  )}
                </p>
              </div>
            ) : (
              ""
            )}
          </>
        ) : (
          <Loader mediumWidth={4} smallWidth={4} subLoaders={2} />
        )}
      </div>
    );
  });

  // Heatmap states
  const [heatmapFilter, setHeatmapFilter] = useState(null);
  const updateheatmapFilter = (newVal) => {
    setHeatmapFilter(newVal);
  };

  const chartOptIn = true;
  let submenu = useMemo(() => {
    return [
      {
        name: t("charts", null, "capital"),
        link: "statistics/charts",
        data_inb_val: t("charts", null, "capital"),
        icon: "fi fi-graph-bar",
        isActive: view === "charts",
        // TODO: Check rbac
        isAccessible: chartOptIn,
      },
      {
        name: t("heatmap", null, "capital"),
        link: "statistics/heatmap",
        data_inb_val: t("heatmap", null, "capital"),
        icon: "fi fi-map",
        isActive: view === "heatmap",
        isAccessible: chartOptIn,
      },
    ];
  }, []);

  return (
    <div id="inb-c-main-dashboard">
      <aside id="inb-c-sidebar" className="filemanager-sidebar" style={{ float: "left" }}>
        <header className="inb-c-left-bar__title">
          <h1 className="inb-page-title">
            {view === "charts" ? t("statistics", null, "capital") : t(view, null, "capital")}
          </h1>
        </header>

        {view === "charts" ? (
          <FilterChart {...getChartFilter} onFilterChange={handleChartsFilter} />
        ) : (
          <FilterHeatMap onFilterChange={updateheatmapFilter} />
        )}
      </aside>
      <div className="dashboard-main">
        <div id="inb-c-main" className="columns">
          <InboomaSubmenu tabs={submenu} smallOnlyTitle={t("statistics", null, "capital")} />
        </div>
        <div className="dashboard-main-content">
          {dashboardLayout === "charts" ? (
            <>
              <div className="dashboard-content-title">
                <p>{t("fleet_statistics")}</p>
              </div>
              <div className="dashboard-content-calculate">
                <div className="dashboard-calculate-total">
                  <p>{t("total_km")}</p>
                  {isSummaryInit ? (
                    <p>
                      {_.isNumber(thisMonth.totalDistance)
                        ? thisMonth.totalDistance.toFixed(2)
                        : "--"}{" "}
                      km
                    </p>
                  ) : (
                    <Loader mediumWidth={3} smallWidth={3} subLoaders={0} />
                  )}
                </div>
                <div className="dashboard-calculate-total">
                  <p>{t("distance_per_booking")}</p>
                  {isSummaryInit ? (
                    <p>
                      {_.isNumber(thisMonth.distancePerBooking)
                        ? thisMonth.distancePerBooking.toFixed(2)
                        : "--"}{" "}
                      km
                    </p>
                  ) : (
                    <Loader mediumWidth={3} smallWidth={3} subLoaders={0} />
                  )}
                </div>
                <div className="dashboard-calculate-total">
                  <p>{t("distance_per_bike")}</p>
                  {isSummaryInit ? (
                    <p>
                      {_.isNumber(thisMonth.distancePerBike)
                        ? thisMonth.distancePerBike.toFixed(2)
                        : "--"}{" "}
                      km
                    </p>
                  ) : (
                    <Loader mediumWidth={3} smallWidth={3} subLoaders={0} />
                  )}
                </div>
                <div className="dashboard-calculate-total">
                  <p>{t("total_bookings")}</p>
                  {isSummaryInit ? (
                    <p>
                      {_.isNumber(thisMonth.totalBooking)
                        ? thisMonth.totalBooking.toFixed(0)
                        : "--"}
                    </p>
                  ) : (
                    <Loader mediumWidth={3} smallWidth={3} subLoaders={0} />
                  )}
                </div>
              </div>
              <div className="dashboard-main-content">
                <div className="dashboard-content-charts">{chartComps}</div>
              </div>
            </>
          ) : (
            <>
              <HeatMap filter={heatmapFilter} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
